/**
 * CompanyMapPromptModal — every model call behind one Company Map
 * (PRD 2026-10-07-company-map-prompt-viewer).
 *
 * A Company Map is one categories call (the roles text alone) and one call per
 * batch of up to 300 titles. One read, `GET …/prompt` of the saved run or of
 * the preview: each call rebuilt server-side and checked against its record.
 * The step list picks a call; the tabs show its system prompt, its request
 * (message + tool) and its attempts; the sidebar shows the recorded call.
 *
 * Native `<dialog>` + `showModal()`, as `PromptViewerModal`. Esc (the
 * `cancel` event) or Close calls `onClose`; focus then returns to the element
 * that opened it.
 */
import { ProgressBar } from "@nu-assist/ui";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, X } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";

import type {
  BctApiError,
  BctClient,
  BctPromptResponse,
  BctPromptStep,
} from "../../clients/bct.client";
import {
  AttemptsPanel,
  CopyJsonButton,
  Kicker,
  Pre,
  SideRow,
} from "../ai-review-prompt/viewerParts";
import { fmtCost, fmtCount, fmtDateTime, fmtSeconds, modelLabel } from "../person-panel/format";

/** Which Company Map: a saved run, or a preview (its `preview_classification_id`). */
export type CompanyMapPromptSource = { kind: "run"; id: string } | { kind: "preview"; id: string };

export interface CompanyMapPromptModalProps {
  client: BctClient;
  source: CompanyMapPromptSource;
  /** The step selected on open (0 = categories, n = batch n). */
  initialIndex?: number;
  onClose: () => void;
}

/** `GET …/bct-runs/{id}/prompt` or `…/bct-previews/{id}/prompt`. Rebuilt
 *  calls of a finished Company Map do not change: cached for the session
 *  slot, no retry (a 404 is "gone", which retrying does not change). Shared
 *  by the modal and the person panel's inline tabs (same key, one read). */
export function useCompanyMapPrompt(
  client: BctClient,
  source: CompanyMapPromptSource,
  enabled = true
) {
  return useQuery<BctPromptResponse, BctApiError>({
    queryKey: ["bct", "prompt", source.kind, source.id],
    queryFn: () =>
      source.kind === "run" ? client.getRunPrompt(source.id) : client.getPreviewPrompt(source.id),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

type ViewerTab = "system" | "request" | "attempts";
const TABS: readonly ViewerTab[] = ["system", "request", "attempts"];

export const MISMATCH_TEXT =
  "The data behind this call changed since it was made; this is a reconstruction.";

const NO_CALL_TEXT = {
  categories:
    "No model call was made for this step: the categories came from the server's cache, " +
    "from an earlier call with the same roles. This is the prompt that call sent.",
  batch: "No recorded model call matches this batch. This is the prompt it would send.",
} as const;

export const UNAVAILABLE_TEXT: Record<NonNullable<BctPromptStep["reason"]>, string> = {
  pre_classification: "This Company Map has no role classification, so there is no prompt to show.",
  specs_not_kept:
    "This Company Map was made before its category definitions were kept, so this call " +
    "cannot be rebuilt.",
  no_titles: "No job title was in scope, so no model call was made.",
};

/** "Categories" or "Batch 2". */
export function stepLabel(index: number): string {
  return index === 0 ? "Categories" : `Batch ${index}`;
}

/** Every step in display order: the categories call, then the batches. */
export function allSteps(prompt: BctPromptResponse): BctPromptStep[] {
  return [prompt.categories_step, ...prompt.assign_batches];
}

/** The JSON a "Copy as JSON" puts on the clipboard: the selected call as shown. */
export function visibleJson(
  source: CompanyMapPromptSource,
  prompt: BctPromptResponse | undefined,
  index: number
): string {
  const step = prompt ? allSteps(prompt)[index] : undefined;
  return JSON.stringify(
    {
      [source.kind === "run" ? "bct_run_id" : "preview_classification_id"]: source.id,
      roles_description: prompt?.roles_description ?? null,
      prompt_version: prompt?.prompt_version ?? null,
      model_id: prompt?.model_id ?? null,
      step: stepLabel(index),
      ...(step ?? {}),
    },
    null,
    2
  );
}

export function StepNote({ step, index }: { step: BctPromptStep; index: number }) {
  if (step.status === "mismatch") {
    return (
      <output
        data-testid="cmp-mismatch"
        className="flex items-start gap-2 rounded-md border border-warning-strong bg-warning-muted px-3 py-2 text-body text-warning-ink"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{MISMATCH_TEXT}</span>
      </output>
    );
  }
  if (step.status === "no_call") {
    return (
      <p
        data-testid="cmp-no-call"
        className="rounded-md border border-border bg-bg-subtle px-3 py-2 text-body text-text-secondary"
      >
        {index === 0 ? NO_CALL_TEXT.categories : NO_CALL_TEXT.batch}
      </p>
    );
  }
  return null;
}

export function CallFacts({
  step,
  prompt,
}: {
  step: BctPromptStep | undefined;
  prompt: BctPromptResponse | undefined;
}) {
  const call = step?.call;
  if (!call) {
    return (
      <p className="text-caption text-text-secondary" data-testid="cmp-call-none">
        {step?.status === "unavailable"
          ? "A call is matched to its rebuilt prompt, and this one cannot be rebuilt."
          : "No recorded model call for this step."}
      </p>
    );
  }
  return (
    <dl className="flex flex-col gap-3" data-testid="cmp-call-facts">
      <SideRow label="Model">
        {modelLabel(call.model_id)}
        {call.model_id && (
          <span className="block break-all font-mono text-text-muted">{call.model_id}</span>
        )}
      </SideRow>
      <SideRow label="Route that answered">
        <span className="break-all font-mono">{call.route ?? "—"}</span>
      </SideRow>
      {prompt?.prompt_version && (
        <SideRow label="Prompt">Company Map prompt {prompt.prompt_version}</SideRow>
      )}
      <SideRow label="Outcome">{call.call_status}</SideRow>
      {call.input_tokens != null && call.output_tokens != null && (
        <SideRow label="Tokens">
          {fmtCount(call.input_tokens)} in · {fmtCount(call.output_tokens)} out
        </SideRow>
      )}
      {call.latency_ms_client != null && (
        <SideRow label="Time">
          {fmtSeconds(call.latency_ms_client)}
          {call.latency_ms_server != null && ` (Bedrock ${fmtSeconds(call.latency_ms_server)})`}
        </SideRow>
      )}
      {call.cost_usd_est != null && (
        <SideRow label="Estimated cost">{fmtCost(call.cost_usd_est)} for the call</SideRow>
      )}
      {call.created_at && <SideRow label="Recorded">{fmtDateTime(call.created_at)}</SideRow>}
      <SideRow label="Ids">
        <span className="block break-all font-mono">req {call.request_id ?? "—"}</span>
        <span className="block break-all font-mono text-text-muted">
          llm_call {call.llm_call_id}
        </span>
      </SideRow>
    </dl>
  );
}

export function CompanyMapPromptModal({
  client,
  source,
  initialIndex = 0,
  onClose,
}: CompanyMapPromptModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const tabsId = useId();
  const [index, setIndex] = useState(initialIndex);
  const [tab, setTab] = useState<ViewerTab>("request");
  const tabRefs = useRef<Record<ViewerTab, HTMLButtonElement | null>>({
    system: null,
    request: null,
    attempts: null,
  });
  const query = useCompanyMapPrompt(client, source);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = dialogRef.current;
    if (el && !el.open) {
      // happy-dom has no showModal(); the attribute is the same dialog, non-modal.
      if (typeof el.showModal === "function") el.showModal();
      else el.setAttribute("open", "");
    }
    closeRef.current?.focus();
    const onCancel = (e: Event) => {
      e.preventDefault();
      onCloseRef.current();
    };
    el?.addEventListener("cancel", onCancel);
    return () => {
      el?.removeEventListener("cancel", onCancel);
      if (el?.open && typeof el.close === "function") el.close();
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);

  const prompt = query.data;
  const steps = prompt ? allSteps(prompt) : [];
  const step = steps[index];
  const attemptCount = step?.call?.attempts?.length ?? 0;
  const tabLabel: Record<ViewerTab, string> = {
    system: "System prompt",
    request: "Request",
    attempts: `Attempts · ${attemptCount}`,
  };

  const onTabKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next =
      TABS[(TABS.indexOf(tab) + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length];
    if (!next) return;
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div className="flex flex-col gap-2 py-6" data-testid="cmp-loading">
        <ProgressBar indeterminate live label="Rebuilding the prompts" />
        <p className="text-caption text-text-muted">Rebuilding the prompts…</p>
      </div>
    );
  } else if (query.isError || !step) {
    body = (
      <p
        data-testid="cmp-error"
        className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
      >
        {query.error?.status === 404
          ? "This Company Map is no longer available (previews are kept for 7 days)."
          : "The prompts could not be loaded. Try again in a moment."}
      </p>
    );
  } else if (step.status === "unavailable") {
    body = (
      <p
        data-testid="cmp-unavailable"
        className="rounded-md border border-border bg-bg-subtle px-4 py-6 text-center text-body text-text-secondary"
      >
        {step.reason ? UNAVAILABLE_TEXT[step.reason] : "This prompt cannot be shown."}
      </p>
    );
  } else {
    body = (
      <div className="flex min-h-0 flex-col gap-3">
        <StepNote step={step} index={index} />
        <div
          role="tablist"
          aria-label="Model call part"
          className="flex gap-1 overflow-x-auto border-b border-border"
          onKeyDown={onTabKeyDown}
        >
          {TABS.map((t) => (
            <button
              key={t}
              ref={(el) => {
                tabRefs.current[t] = el;
              }}
              type="button"
              role="tab"
              id={`${tabsId}-tab-${t}`}
              aria-selected={tab === t}
              aria-controls={`${tabsId}-panel`}
              tabIndex={tab === t ? 0 : -1}
              data-testid={`cmp-tab-${t}`}
              onClick={() => setTab(t)}
              className={`whitespace-nowrap border-b-2 px-2 py-2 text-label focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                tab === t
                  ? "border-accent text-accent-ink"
                  : "border-transparent text-text-secondary hover:text-text"
              }`}
            >
              {tabLabel[t]}
            </button>
          ))}
        </div>
        <div
          role="tabpanel"
          id={`${tabsId}-panel`}
          aria-labelledby={`${tabsId}-tab-${tab}`}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region must be focusable to scroll by keyboard
          tabIndex={0}
          className="min-h-0 overflow-auto rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          {tab === "system" && <Pre testId="cmp-system">{step.system ?? ""}</Pre>}
          {tab === "request" && (
            <div className="flex flex-col gap-3">
              <Pre testId="cmp-user-message">{step.user_message ?? ""}</Pre>
              <Kicker>Tool</Kicker>
              <Pre testId="cmp-tool-config">
                {JSON.stringify(step.tool_config ?? null, null, 2)}
              </Pre>
            </div>
          )}
          {tab === "attempts" && <AttemptsPanel call={step.call ?? undefined} />}
        </div>
      </div>
    );
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      data-testid="company-map-prompt-viewer"
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
      className="fixed inset-0 m-auto hidden max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-border bg-surface p-0 text-text shadow-lg backdrop:bg-black/50 open:flex"
    >
      <header className="flex flex-col gap-3 border-b border-border px-6 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <Kicker>Company Map · Model calls</Kicker>
          <h2 id={titleId} className="text-h3 font-semibold text-text" data-testid="cmp-title">
            {source.kind === "run" ? "Saved Company Map" : "Company Map preview"}
            {prompt?.roles_description ? ` — ${prompt.roles_description}` : ""}
          </h2>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <CopyJsonButton text={visibleJson(source, prompt, index)} />
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="cmp-close"
            className="rounded-md p-1 text-text-muted hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto lg:flex-row">
        {steps.length > 0 && (
          <nav
            aria-label="Model calls"
            className="border-b border-border px-4 py-4 lg:w-48 lg:shrink-0 lg:border-b-0 lg:border-r"
          >
            <ul className="flex flex-row flex-wrap gap-1 lg:flex-col">
              {steps.map((s, i) => (
                <li key={stepLabel(i)}>
                  <button
                    type="button"
                    data-testid={i === 0 ? "cmp-step-categories" : `cmp-step-batch-${i}`}
                    aria-current={i === index ? "true" : undefined}
                    onClick={() => setIndex(i)}
                    className={`flex w-full flex-col rounded-md px-2 py-1.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                      i === index
                        ? "bg-accent-muted text-accent-ink"
                        : "text-text hover:bg-surface-raised"
                    }`}
                  >
                    <span className="text-label font-medium">{stepLabel(i)}</span>
                    <span className="text-caption text-text-muted">
                      {i === 0 ? "" : `${fmtCount(s.title_count)} titles · `}
                      {s.status.replace("_", " ")}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-4 px-6 py-4">{body}</div>
        <aside
          aria-label="The recorded call"
          className="border-t border-border px-6 py-4 lg:w-72 lg:shrink-0 lg:border-l lg:border-t-0"
        >
          <h3 className="mb-3 text-label font-semibold text-text">The recorded call</h3>
          {query.isPending ? (
            <p className="text-caption text-text-muted">Loading…</p>
          ) : (
            <CallFacts step={step} prompt={prompt} />
          )}
        </aside>
      </div>
    </dialog>
  );
}
